import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Seo } from "../../lib/seo.js";
import {
  initAudio,
  playBustThud,
  playDoorSwing,
  playKeyClick,
  playLockCrack,
  playTick,
  playWinSting,
  setSoundMuted,
} from "../../lib/sound.js";
import { Dust, Grain, LightCone, Vignette } from "../common/core.js";
import {
  BetInput,
  FairnessFooter,
  MuteToggle,
  PrimaryCta,
  SectionHeader,
  Wallet,
} from "../common/Controls.js";
import { SlotRow } from "./Safe.js";
import {
  GuessHistoryRow,
  HowToCrack,
  Keypad,
  PayoutLadder,
  StatusPill,
  type LadderMode,
  type StatusKind,
} from "./Controls.js";
import { CashedOutBanner, CrackedBanner, SealedBanner } from "./Overlays.js";
import {
  CODE_LEN,
  MAX_GUESSES,
  PAYOUT_BY_ATTEMPT,
  ROUND_TIME_S,
  cashoutMultiplier,
  computePegs,
  deriveCode,
  multiplierForAttempt,
  type CodeTuple,
  type Guess,
} from "./engine.js";
import { rgs, setRgsAuthToken } from "../../lib/rgs-client.js";
import { useGame } from "../../store.js";
import { track } from "../../lib/analytics.js";

const DEFAULT_BET = 0.001;

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

/**
 * Cipher Crack — Mastermind-style code-cracker.
 *
 * Phase machine:
 *   idle → live → cracked | sealed | cashed_out → idle
 *
 * The server commits a 4-digit code on LAUNCH. Player has 4 attempts in
 * 30 seconds to crack it. Each guess returns peg feedback (gold/silver/
 * miss). Crack on attempt N → payout = stake × [20, 5, 2.5, 1.3][N-1].
 * 4 attempts spent OR 30s expired without a crack → sealed (lose stake).
 * Player can also cash out for a partial refund any time after at least
 * one wrong guess. All audio cues fire on the resolution moment, not the
 * input moment.
 */
export function CipherCrack() {
  // ---- session-level state from /rgs/session/launch -----------------------
  const sessionId = useGame((s) => s.sessionId);
  const balance = useGame((s) => s.balance);
  const currency = useGame((s) => s.currency);
  const sessionServerSeedHash = useGame((s) => s.serverSeedHash);
  const sessionClientSeed = useGame((s) => s.clientSeed);
  const locale = useGame((s) => s.locale);
  const storeGameSlug = useGame((s) => s.gameSlug);
  const hydrateFromLaunch = useGame((s) => s.hydrateFromLaunch);
  const setBalance = useGame((s) => s.setBalance);
  const setOperator = useGame((s) => s.setOperator);
  const clearSession = useGame((s) => s.clearSession);
  const refillDemoBalance = useGame((s) => s.refillDemoBalance);

  const [booting, setBooting] = useState(true);
  const compact = useMediaQuery("(max-width: 760px)");

  // Per-round seed/nonce — server returns these on /rgs/round/open. Server
  // reveals the seed at open for CC so the client can compute pegs locally
  // and show immediate feedback. Server re-validates on resolve.
  const [roundIdState, setRoundIdState] = useState<string | null>(null);
  const [roundServerSeed, setRoundServerSeed] = useState<string | null>(null);
  const [roundClientSeed, setRoundClientSeed] = useState<string | null>(null);
  const [roundNonce, setRoundNonce] = useState<number>(0);
  const [roundServerSeedHash, setRoundServerSeedHash] = useState<string | null>(null);

  const [muted, setMuted] = useState(false);
  useEffect(() => {
    setSoundMuted(muted);
  }, [muted]);

  // Mirror the session JWT into the rgs-client so /rgs/round/open and
  // /rgs/round/resolve carry Authorization: Bearer <jwt>. Without this,
  // launch succeeds but every subsequent round-route returns 401.
  const sessionToken = useGame((s) => s.token);
  useEffect(() => {
    setRgsAuthToken(sessionToken);
  }, [sessionToken]);

  // Bootstrap a Cipher Crack session on mount. Same pattern as PP/GameView.
  // If the player navigated here from a different game the existing session
  // is bound to that slug — server 400s on /rgs/round/open with mismatched
  // gameSlug. Wipe and re-launch as cipher-crack.
  useEffect(() => {
    if (sessionId && storeGameSlug === "cipher-crack") {
      setBooting(false);
      return;
    }
    if (sessionId && storeGameSlug !== "cipher-crack") {
      clearSession();
    }
    const params = new URLSearchParams(location.search);
    const opId = params.get("operator") ?? "demo";
    const loc = params.get("locale") ?? locale;
    const cur =
      (params.get("currency") as "BTC" | "ETH" | "SOL" | "USDT" | "USD" | null) ?? "BTC";
    setOperator(opId, loc);
    rgs
      .launch({
        operatorId: opId,
        playerId: `demo_${Math.random().toString(36).slice(2, 8)}`,
        currency: cur,
        locale: loc,
        gameSlug: "cipher-crack",
      })
      .then((res) => {
        hydrateFromLaunch(res);
        setBooting(false);
        track("session_launched", { gameSlug: "cipher-crack" });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("cipher-crack launch failed", err);
        setBooting(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- round-level state ----
  type Phase = "idle" | "live" | "cracked" | "sealed" | "cashed_out";
  const [phase, setPhase] = useState<Phase>("idle");
  const [bet, setBet] = useState(DEFAULT_BET);
  const [code, setCode] = useState<CodeTuple | null>(null);
  const [currentGuess, setCurrentGuess] = useState<Array<number | null>>([null, null, null, null]);
  const [guesses, setGuesses] = useState<Guess[]>([]);
  /** Multiplier locked in via cashout (null until cashout fires). */
  const [cashoutMult, setCashoutMult] = useState<number | null>(null);
  const [fairOpen, setFairOpen] = useState(false);
  /** Wall-clock deadline (performance.now() ms) for the current round. */
  const [roundEndsAt, setRoundEndsAt] = useState<number | null>(null);
  /** Whole seconds remaining in the round (display only). */
  const [timeLeft, setTimeLeft] = useState<number>(ROUND_TIME_S);

  // Pending audio timers — clear on reset so a rapid PLAY AGAIN doesn't
  // have last round's tail fire over the new launch.
  const pendingAudioTimers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const scheduleAudio = useCallback((fn: () => void, delayMs: number) => {
    const id = setTimeout(() => {
      pendingAudioTimers.current = pendingAudioTimers.current.filter((x) => x !== id);
      fn();
    }, delayMs);
    pendingAudioTimers.current.push(id);
  }, []);
  const clearPendingAudio = useCallback(() => {
    for (const id of pendingAudioTimers.current) clearTimeout(id);
    pendingAudioTimers.current = [];
  }, []);

  // Active slot = first null index in currentGuess. If all filled, returns 4
  // (used to enable the ENTER key).
  const activeIndex = useMemo(() => {
    const idx = currentGuess.findIndex((d) => d === null);
    return idx === -1 ? CODE_LEN : idx;
  }, [currentGuess]);

  const currentAttempt = guesses.length + 1; // 1-based
  const enterArmed = phase === "live" && activeIndex === CODE_LEN;

  // ---- handlers ----
  // LAUNCH ROUND → /rgs/round/open. Server commits seed, returns roundId +
  // revealed serverSeed (so the client can derive the code locally for peg
  // feedback). Server re-validates the player's guesses on resolve.
  const onLaunch = useCallback(async () => {
    if (phase !== "idle") return;
    if (!sessionId) return;
    if (!Number.isFinite(bet) || bet <= 0 || bet > balance) return;
    clearPendingAudio();
    initAudio();
    playLockCrack();
    try {
      const res = await rgs.open({ sessionId, betAmount: bet });
      setBalance(res.balance);
      if (!res.serverSeed) {
        // eslint-disable-next-line no-console
        console.error("cipher-crack open: missing serverSeed in response");
        return;
      }
      setRoundIdState(res.roundId);
      setRoundServerSeed(res.serverSeed);
      setRoundClientSeed(res.clientSeed);
      setRoundNonce(res.nonce);
      setRoundServerSeedHash(res.serverSeedHash);
      const newCode = deriveCode(res.serverSeed, res.clientSeed, res.nonce);
      setCode(newCode);
      setGuesses([]);
      setCurrentGuess([null, null, null, null]);
      setCashoutMult(null);
      setRoundEndsAt(performance.now() + ROUND_TIME_S * 1000);
      setTimeLeft(ROUND_TIME_S);
      setPhase("live");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("cipher-crack open failed", err);
    }
  }, [phase, sessionId, bet, balance, clearPendingAudio, setBalance]);

  // Helper: send the resolve to the server. Builds the discriminated-union
  // request with the player's guesses (as 4-digit strings), the cashedOut
  // flag, and `cracked` (true if a guess fully matched). Server pays out.
  const resolveRound = useCallback(
    async (
      finalGuesses: ReadonlyArray<Guess>,
      cashedOut: boolean,
      cracked: boolean,
    ): Promise<void> => {
      if (!sessionId || !roundIdState) return;
      try {
        const res = await rgs.resolve({
          sessionId,
          roundId: roundIdState,
          gameSlug: "cipher-crack",
          guesses: finalGuesses.map((g) => g.digits.join("")),
          cracked,
          cashedOut,
        });
        setBalance(res.balance);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("cipher-crack resolve failed", err);
      }
    },
    [sessionId, roundIdState, setBalance],
  );

  const onReset = useCallback(() => {
    clearPendingAudio();
    setPhase("idle");
    setCode(null);
    setGuesses([]);
    setCurrentGuess([null, null, null, null]);
    setCashoutMult(null);
    setRoundEndsAt(null);
    setTimeLeft(ROUND_TIME_S);
    setRoundIdState(null);
    setRoundServerSeed(null);
    setRoundClientSeed(null);
    setRoundServerSeedHash(null);
    playKeyClick();
  }, [clearPendingAudio]);

  /** Available cashout multiplier given current state. 0 = not allowed. */
  const cashoutAvailableMult = phase === "live" ? cashoutMultiplier(guesses.length) : 0;
  const cashoutEnabled = cashoutAvailableMult > 0;

  const onCashout = useCallback(() => {
    if (phase !== "live") return;
    const wrongs = guesses.length;
    const mult = cashoutMultiplier(wrongs);
    if (mult <= 0) return;
    initAudio();
    playKeyClick();
    playWinSting();
    scheduleAudio(playDoorSwing, 100);
    setCashoutMult(mult);
    setPhase("cashed_out");
    // Server is authoritative for the payout — it'll credit the wallet on
    // resolve. The optimistic UI shows the cashout multiplier immediately;
    // setBalance from the server response on resolve reconciles.
    void resolveRound(guesses, true, false);
  }, [bet, guesses, phase, scheduleAudio, resolveRound]);

  const onDigit = useCallback(
    (d: number) => {
      if (phase !== "live") return;
      if (activeIndex >= CODE_LEN) return;
      initAudio();
      playKeyClick();
      setCurrentGuess((g) => {
        const next = [...g];
        next[activeIndex] = d;
        return next;
      });
    },
    [phase, activeIndex],
  );

  const onClear = useCallback(() => {
    if (phase !== "live") return;
    // If all slots empty, no-op. Otherwise clear the LAST filled slot
    // (one-by-one backspace). If everything's filled, clear all.
    setCurrentGuess((g) => {
      const lastFilled = (() => {
        for (let i = g.length - 1; i >= 0; i -= 1) if (g[i] !== null) return i;
        return -1;
      })();
      if (lastFilled === -1) return g;
      const next = [...g];
      next[lastFilled] = null;
      return next;
    });
    playKeyClick();
  }, [phase]);

  const onSubmit = useCallback(() => {
    if (phase !== "live" || !code || activeIndex !== CODE_LEN) return;
    const digits = currentGuess as ReadonlyArray<number>;
    const pegs = computePegs(digits, code);
    const guess: Guess = {
      digits: [digits[0]!, digits[1]!, digits[2]!, digits[3]!],
      pegs,
    };
    const nextGuesses = [...guesses, guess];
    setGuesses(nextGuesses);
    setCurrentGuess([null, null, null, null]);
    const cracked = pegs.every((p) => p === "gold");
    if (cracked) {
      playWinSting();
      scheduleAudio(playDoorSwing, 120);
      setPhase("cracked");
      void resolveRound(nextGuesses, false, true);
    } else if (nextGuesses.length >= MAX_GUESSES) {
      playBustThud();
      scheduleAudio(playLockCrack, 80);
      setPhase("sealed");
      void resolveRound(nextGuesses, false, false);
    } else {
      // Keep playing — soft click, no full bust audio.
      playKeyClick();
    }
  }, [activeIndex, code, currentGuess, guesses, phase, scheduleAudio, resolveRound]);

  // ---- round timer ----
  // 1Hz countdown during phase=live. Plays a tick sound each second whose
  // intensity ramps from 0 (calm, low pitch + no drone) at launch to 1
  // (panic, high pitch + sub-bass drone + downward pitch sweep) at 0:00.
  // Below 3 seconds, fires an extra "heartbeat" half-tick at the 0.5s mark
  // for a double-tap urgency feel. Auto-busts to sealed at 0.
  //
  // We read `guesses` and `resolveRound` through refs inside the interval so
  // they don't appear in the effect dep array. Including them re-created the
  // interval on every keystroke — overlapping timers fired multiple ticks per
  // second and the audio compounded into a sub-bass garble.
  const guessesRef = useRef(guesses);
  useEffect(() => {
    guessesRef.current = guesses;
  }, [guesses]);
  const resolveRoundRef = useRef(resolveRound);
  useEffect(() => {
    resolveRoundRef.current = resolveRound;
  }, [resolveRound]);
  useEffect(() => {
    if (phase !== "live" || roundEndsAt === null) return;
    const halfTickTimers: Array<ReturnType<typeof setTimeout>> = [];
    const tick = (): void => {
      const remainingMs = roundEndsAt - performance.now();
      const remaining = Math.max(0, Math.ceil(remainingMs / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        playBustThud();
        scheduleAudio(playLockCrack, 80);
        setPhase("sealed");
        // Settle on the server with whatever guesses were made before
        // timeout — counts as no-cashout, no-crack, stake forfeit.
        void resolveRoundRef.current(guessesRef.current, false, false);
        return;
      }
      // Intensity ramps linearly across the round: 0 at launch, ~1 at end.
      // Use elapsed-fraction so the lerp is continuous within each second.
      const elapsed = ROUND_TIME_S * 1000 - remainingMs;
      const intensity = Math.max(0, Math.min(1, elapsed / (ROUND_TIME_S * 1000)));
      playTick(intensity);
      // Heartbeat double-tap in the last 3 seconds — extra panic-tick at
      // the half-second mark with even higher intensity.
      if (remaining <= 3) {
        const half = setTimeout(() => {
          halfTickTimers.splice(halfTickTimers.indexOf(half), 1);
          playTick(Math.min(1, intensity + 0.15));
        }, 500);
        halfTickTimers.push(half);
      }
    };
    const id = setInterval(tick, 1000);
    return () => {
      clearInterval(id);
      for (const t of halfTickTimers) clearTimeout(t);
    };
  }, [phase, roundEndsAt, scheduleAudio]);

  // ---- keyboard ----
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (phase === "live") {
        if (e.key >= "0" && e.key <= "9") {
          e.preventDefault();
          onDigit(Number(e.key));
          return;
        }
        if (e.key === "Backspace" || e.key === "Delete") {
          e.preventDefault();
          onClear();
          return;
        }
        if (e.key === "Enter" && enterArmed) {
          e.preventDefault();
          onSubmit();
          return;
        }
        if ((e.key === "c" || e.key === "C") && cashoutEnabled) {
          e.preventDefault();
          onCashout();
          return;
        }
      }
      if (phase === "idle") {
        if ((e.key === " " || e.key === "Enter") && bet > 0 && bet <= balance) {
          e.preventDefault();
          onLaunch();
          return;
        }
      }
      if (phase === "cracked" || phase === "sealed" || phase === "cashed_out") {
        if (e.key === "Enter" || e.key === "Escape") {
          e.preventDefault();
          onReset();
          return;
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, enterArmed, cashoutEnabled, onDigit, onClear, onSubmit, onLaunch, onReset, onCashout, bet, balance]);

  // ---- derived UI props ----
  const statusKind: StatusKind =
    phase === "idle"
      ? "idle"
      : phase === "live"
        ? "live"
        : phase === "cracked"
          ? "cracked"
          : phase === "cashed_out"
            ? "cashed_out"
            : "sealed";
  // The PayoutLadder component doesn't have a "cashed_out" mode — render
  // it as "sealed" (dim past rungs) since the player is no longer chasing
  // the rungs.
  const ladderMode: LadderMode =
    phase === "idle"
      ? "idle"
      : phase === "cracked"
        ? "cracked"
        : phase === "sealed" || phase === "cashed_out"
          ? "sealed"
          : "live";
  const ladderAttempt = phase === "cracked" ? guesses.length : currentAttempt;

  // ---- CTA configuration ----
  const insufficientForBet = bet > balance || !Number.isFinite(bet) || bet <= 0;
  const triggerRefill = useCallback(() => {
    track("refill_demo_balance", { gameSlug: "cipher-crack" });
    void refillDemoBalance("cipher-crack");
  }, [refillDemoBalance]);

  let ctaLabel: string;
  let ctaSub: string | null;
  let ctaKind: "gold" | "green" | "ghost";
  let ctaDisabled = false;
  let ctaOnClick: () => void;
  if (phase === "idle") {
    if (bet > balance) {
      ctaLabel = "↻ REFILL DEMO BALANCE";
      ctaSub = `Bet ${bet.toFixed(8)} > balance ${balance.toFixed(8)} ${currency}`;
      ctaKind = "gold";
      ctaDisabled = false;
      ctaOnClick = triggerRefill;
    } else {
      ctaLabel = "LAUNCH ROUND";
      ctaSub = "4 attempts · 30s · payout 20× → 1.3×";
      ctaKind = "gold";
      ctaDisabled = !Number.isFinite(bet) || bet <= 0;
      ctaOnClick = onLaunch;
    }
  } else if (phase === "live") {
    ctaLabel = enterArmed ? "ENTER GUESS" : "DIAL IN 4 DIGITS";
    ctaSub = enterArmed
      ? `attempt 0${currentAttempt} · ${PAYOUT_BY_ATTEMPT[currentAttempt - 1]?.toFixed(1)}×`
      : `slot ${activeIndex + 1} of 4 · keypad armed`;
    ctaKind = enterArmed ? "gold" : "ghost";
    ctaDisabled = !enterArmed;
    ctaOnClick = onSubmit;
  } else if (phase === "cracked") {
    const winAttempt = guesses.length;
    const mult = multiplierForAttempt(winAttempt);
    const profit = bet * mult - bet;
    if (insufficientForBet) {
      ctaLabel = "↻ REFILL DEMO BALANCE";
      ctaSub = `Balance ${balance.toFixed(8)} ${currency}`;
      ctaKind = "gold";
      ctaOnClick = triggerRefill;
    } else {
      ctaLabel = "PLAY AGAIN";
      ctaSub = `+${profit.toFixed(8)} ${currency} @ ${mult.toFixed(2)}×`;
      ctaKind = "green";
      ctaOnClick = onReset;
    }
  } else if (phase === "cashed_out") {
    const mult = cashoutMult ?? 0;
    const delta = bet * mult - bet;
    const sign = delta >= 0 ? "+" : "−";
    if (insufficientForBet) {
      ctaLabel = "↻ REFILL DEMO BALANCE";
      ctaSub = `Balance ${balance.toFixed(8)} ${currency}`;
      ctaKind = "gold";
      ctaOnClick = triggerRefill;
    } else {
      ctaLabel = "PLAY AGAIN";
      ctaSub = `cashed out @ ${mult.toFixed(2)}× · ${sign}${Math.abs(delta).toFixed(8)} ${currency}`;
      ctaKind = mult >= 1 ? "green" : "gold";
      ctaOnClick = onReset;
    }
  } else {
    // sealed
    if (insufficientForBet) {
      ctaLabel = "↻ REFILL DEMO BALANCE";
      ctaSub = `Balance ${balance.toFixed(8)} ${currency}`;
      ctaKind = "gold";
      ctaOnClick = triggerRefill;
    } else {
      ctaLabel = "PLAY AGAIN";
      ctaSub = `vault sealed · −${bet.toFixed(8)} ${currency}`;
      ctaKind = "gold";
      ctaOnClick = onReset;
    }
  }

  // Stage hue: red on bust, warm gold otherwise (cashed_out uses warm + the
  // cyan banner overlays the stage with its own light wash).
  const stageHue: "warm" | "red" = phase === "sealed" ? "red" : "warm";

  return (
    <>
      <Seo
        title="Cipher Crack"
        description="A provably-fair code-cracking game. Match a hidden 4-digit code in 4 guesses under 30 seconds. Payouts up to 20×."
        path="/play/cipher-crack"
      />
      <div
        className="cc-shell"
        style={{
          position: "relative",
          // 100dvh handles iOS dynamic toolbar; safe-area-inset-bottom keeps
          // the sticky CTA clear of the home indicator. 100vh is the fallback.
          height: "100dvh",
          minHeight: "100vh",
          paddingBottom: "env(safe-area-inset-bottom)",
          background: "var(--obsidian)",
          color: "var(--fg-1)",
          fontFamily: "var(--font-ui)",
          overflow: "hidden",
        }}
      >
        {/* Atmospherics */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
          <LightCone hue={stageHue} intensity={1} />
          <Vignette strength={1} />
          <Dust count={42} intensity={phase === "sealed" ? 0.35 : 0.7} color={phase === "sealed" ? "red" : "gold"} />
          <Grain opacity={0.06} />
        </div>

        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
          {/* TOP BAR — operator-neutral. No studio branding so the iframe
               drops cleanly into any operator's lobby. Just the game name. */}
          <header style={topBarStyle}>
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 14,
                letterSpacing: "0.06em",
                color: "var(--fg-1)",
                textTransform: "uppercase",
              }}
            >
              Cipher Crack
            </span>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span
                className="pp-hide-mobile"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--fg-3)",
                  letterSpacing: 1,
                }}
              >
                ROUND #{roundNonce}
              </span>
              <MuteToggle muted={muted} onClick={() => setMuted((m) => !m)} />
              <Wallet balance={balance} />
            </div>
          </header>

          {/* GRID — collapses to single column on phones so the keypad fits. */}
          <div
            className="cc-grid"
            style={compact ? { ...gridStyle, gridTemplateColumns: "1fr" } : gridStyle}
          >
            {/* LEFT — setup + how-to-crack */}
            {!compact && (
              <aside className="cc-sidebar cc-sidebar-left" style={leftPanelStyle}>
                <SectionHeader title="Setup" no="01" />
                <BetInput amount={bet} onChange={setBet} disabled={phase !== "idle"} />
                <div style={{ height: 1, background: "var(--line)" }} />
                <HowToCrack />
              </aside>
            )}

            {/* CENTER — stage */}
            <main style={mainStyle}>
              {booting ? (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    display: "grid",
                    placeItems: "center",
                    gap: 12,
                    padding: 32,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--fg-3)",
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: "var(--gold-bright)",
                      boxShadow: "0 0 12px var(--gold-bright)",
                      animation: "pp-bump 900ms ease-in-out infinite",
                    }}
                  />
                  <span>Committing seed…</span>
                </div>
              ) : (
                <>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <StatusPill kind={statusKind} attempt={currentAttempt} total={MAX_GUESSES} />
                {phase === "live" && (
                  <div
                    aria-label={`Round timer ${timeLeft} seconds`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 10px",
                      background: "rgba(11,14,22,0.7)",
                      border: `1px solid ${
                        timeLeft <= 5
                          ? "rgba(224,69,69,0.55)"
                          : timeLeft <= 10
                            ? "rgba(241,203,107,0.55)"
                            : "rgba(107,198,224,0.45)"
                      }`,
                      borderRadius: 2,
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 1,
                      color:
                        timeLeft <= 5
                          ? "var(--red-bright)"
                          : timeLeft <= 10
                            ? "var(--gold-bright)"
                            : "var(--cyan-bright)",
                      // Pulse the border subtly in the last 5 seconds.
                      animation: timeLeft <= 5 ? "pp-bump 600ms ease-in-out infinite" : undefined,
                    }}
                  >
                    <span style={{ fontSize: 9, opacity: 0.7, letterSpacing: 1.5 }}>TIMER</span>
                    <span>
                      00:{String(timeLeft).padStart(2, "0")}
                    </span>
                  </div>
                )}
              </div>
              {/* Combination dials */}
              <div style={{ marginTop: 4 }}>
                <SlotRow
                  digits={currentGuess}
                  activeIndex={activeIndex}
                  mode="input"
                  size="md"
                />
              </div>
              {/* Caption */}
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--fg-3)",
                  letterSpacing: 0.5,
                  marginTop: 2,
                  textAlign: "center",
                }}
              >
                {phase === "idle" && "Place a bet · launch the round to commit a code"}
                {phase === "live" && enterArmed && "All 4 dials set — ENTER to submit"}
                {phase === "live" && !enterArmed && `Dial ${activeIndex + 1} active — keypad armed`}
                {phase === "cracked" && "Tumblers locked"}
                {phase === "sealed" && (
                  timeLeft <= 0 ? "Time expired · vault sealed" : `${MAX_GUESSES} of ${MAX_GUESSES} attempts spent`
                )}
              </div>
              {/* Two-column: keypad + ledger */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: 18,
                  alignItems: "flex-start",
                  marginTop: 8,
                  width: "100%",
                  maxWidth: 640,
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {/* Keypad */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <div
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: 2,
                      color: "var(--fg-3)",
                      textTransform: "uppercase",
                      textAlign: "center",
                    }}
                  >
                    Keypad
                  </div>
                  <Keypad
                    onDigit={onDigit}
                    onClear={onClear}
                    onSubmit={onSubmit}
                    enterActive={enterArmed}
                    disabled={phase !== "live"}
                    buttonW={52}
                    buttonH={42}
                    gap={4}
                  />
                </div>
                {/* Ledger */}
                <div style={{ display: "flex", flexDirection: "column", gap: 0, minWidth: 0, minHeight: 0 }}>
                  <div
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: 2,
                      color: "var(--fg-3)",
                      textTransform: "uppercase",
                      marginBottom: 6,
                      paddingLeft: 2,
                    }}
                  >
                    Ledger · Past attempts
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                    }}
                  >
                    {guesses.length === 0 ? (
                      <div
                        style={{
                          padding: "12px 10px",
                          background: "rgba(17,21,31,0.4)",
                          border: "1px dashed var(--line-bone)",
                          borderRadius: 3,
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--fg-4)",
                          textAlign: "center",
                          letterSpacing: 0.5,
                        }}
                      >
                        No attempts yet · 4 chances to crack
                      </div>
                    ) : (
                      guesses.map((g, i) => (
                        <GuessHistoryRow
                          key={i}
                          attempt={i + 1}
                          digits={g.digits}
                          pegs={g.pegs}
                          dim
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
              {/* Resolution overlays */}
              {phase === "cracked" && code && (
                <CrackedBanner
                  attempt={guesses.length}
                  multiplier={multiplierForAttempt(guesses.length)}
                  payout={bet * multiplierForAttempt(guesses.length)}
                  code={code}
                />
              )}
              {phase === "sealed" && code && <SealedBanner code={code} stake={bet} />}
              {phase === "cashed_out" && code && cashoutMult !== null && (
                <CashedOutBanner
                  multiplier={cashoutMult}
                  payout={bet * cashoutMult}
                  attemptCashedAt={guesses.length + 1}
                  code={code}
                />
              )}
                </>
              )}
            </main>

            {/* RIGHT — payouts + round hash */}
            {!compact && (
            <aside className="cc-sidebar cc-sidebar-right" style={rightPanelStyle}>
              <SectionHeader title="Payouts" no="02" />
              <PayoutLadder currentAttempt={ladderAttempt} mode={ladderMode} />
              <div style={{ height: 1, background: "var(--line)" }} />
              <SectionHeader title="Round Hash" no="03" />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  background: "rgba(17,21,31,0.7)",
                  border: "1px solid var(--line)",
                  borderRadius: 3,
                  padding: 8,
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: 1.5,
                    color: "var(--fg-3)",
                    textTransform: "uppercase",
                  }}
                >
                  Server seed hash
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--fg-1)",
                    wordBreak: "break-all",
                  }}
                >
                  {roundServerSeedHash
                    ? `0x${roundServerSeedHash.slice(0, 6)}…${roundServerSeedHash.slice(-6)}`
                    : sessionServerSeedHash
                      ? `0x${sessionServerSeedHash.slice(0, 6)}…${sessionServerSeedHash.slice(-6)}`
                      : "—"}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-3)" }}>nonce</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-1)" }}>#{roundNonce}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-3)" }}>client seed</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-1)" }}>{roundClientSeed ?? sessionClientSeed ?? "—"}</span>
                </div>
              </div>
            </aside>
            )}
          </div>

          {/* FOOTER */}
          <footer style={footerStyle}>
            <div className="pp-fairness-slot">
              <FairnessFooter onVerify={() => setFairOpen(true)} />
            </div>
            {/* CASH OUT button — only during live phase, after at least one
                wrong guess. Sits left of the main CTA. */}
            {phase === "live" && cashoutEnabled && (
              <button
                type="button"
                onClick={onCashout}
                aria-label={`Cash out at ${cashoutAvailableMult.toFixed(2)} times stake`}
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  height: 36,
                  padding: "0 14px",
                  background:
                    cashoutAvailableMult >= 1
                      ? "linear-gradient(180deg, rgba(91,208,138,0.18) 0%, rgba(36,80,52,0.10) 100%)"
                      : "linear-gradient(180deg, rgba(107,198,224,0.16) 0%, rgba(18,40,52,0.10) 100%)",
                  border: `1px solid ${cashoutAvailableMult >= 1 ? "var(--green-bright)" : "var(--cyan-bright)"}`,
                  borderRadius: 3,
                  color: cashoutAvailableMult >= 1 ? "var(--green-bright)" : "var(--cyan-bright)",
                  cursor: "pointer",
                  fontFamily: "var(--font-ui)",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2 }}>CASH OUT</span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                  }}
                >
                  {cashoutAvailableMult.toFixed(2)}×
                </span>
              </button>
            )}
            <div className="pp-cta-slot" style={{ width: "100%" }}>
              <PrimaryCta
                label={ctaLabel}
                sub={ctaSub}
                kind={ctaKind}
                disabled={ctaDisabled}
                height={36}
                onClick={ctaOnClick}
              />
            </div>
            <div
              className="pp-hide-mobile"
              style={{
                display: "inline-flex",
                gap: 12,
                alignItems: "center",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--fg-4)",
                whiteSpace: "nowrap",
              }}
            >
              <span>0–9 dial</span>
              <span>↵ submit</span>
              <span>⌫ clear</span>
              <span>c cash</span>
            </div>
          </footer>
        </div>

        {/* Verify modal */}
        {fairOpen && (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => setFairOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100,
              background: "rgba(6,8,15,0.72)",
              backdropFilter: "blur(8px)",
              display: "grid",
              placeItems: "center",
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "var(--bg-1)",
                border: "1px solid var(--line)",
                borderRadius: 6,
                maxWidth: 480,
                width: "100%",
                padding: 24,
                color: "var(--fg-1)",
                fontFamily: "var(--font-ui)",
                maxHeight: "80vh",
                overflow: "auto",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontFamily: "var(--font-display)",
                  fontSize: 18,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Verify this round
              </h3>
              <p style={{ marginTop: 12, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
                The 4-digit code is derived deterministically from the server seed (committed before
                play), client seed, and nonce. The seed reveals when the round resolves so you can
                re-derive the same code from the same inputs.
              </p>
              <div
                style={{
                  marginTop: 14,
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--fg-2)",
                  lineHeight: 1.7,
                }}
              >
                <div>
                  <span style={{ color: "var(--fg-3)" }}>seed hash:</span>{" "}
                  {roundServerSeedHash ?? sessionServerSeedHash ?? "—"}
                </div>
                <div>
                  <span style={{ color: "var(--fg-3)" }}>client:</span>{" "}
                  {roundClientSeed ?? sessionClientSeed ?? "—"}
                </div>
                <div>
                  <span style={{ color: "var(--fg-3)" }}>nonce:</span> #{roundNonce}
                </div>
                {(phase === "cracked" || phase === "sealed" || phase === "cashed_out") && code && (
                  <>
                    <div style={{ marginTop: 10, color: "var(--gold-bright)" }}>
                      round resolved · {phase.replace("_", " ").toUpperCase()}
                    </div>
                    <div>
                      <span style={{ color: "var(--fg-3)" }}>code:</span>{" "}
                      <span style={{ color: "var(--gold-bright)", fontWeight: 700 }}>
                        {code.join(" ")}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: "var(--fg-3)" }}>guesses used:</span> {guesses.length} of {MAX_GUESSES}
                    </div>
                    {phase === "cashed_out" && cashoutMult !== null && (
                      <div>
                        <span style={{ color: "var(--fg-3)" }}>cashout mult:</span>{" "}
                        <span style={{ color: "var(--cyan-bright)", fontWeight: 700 }}>
                          {cashoutMult.toFixed(2)}×
                        </span>
                      </div>
                    )}
                  </>
                )}
                {phase !== "cracked" && phase !== "sealed" && phase !== "cashed_out" && (
                  <div style={{ marginTop: 10, color: "var(--fg-3)" }}>
                    Server seed sealed until round resolves.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setFairOpen(false)}
                style={{
                  marginTop: 16,
                  padding: "8px 16px",
                  background: "var(--bg-2)",
                  border: "1px solid var(--line)",
                  color: "var(--fg-1)",
                  fontFamily: "var(--font-ui)",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 1,
                  borderRadius: 3,
                  cursor: "pointer",
                }}
              >
                CLOSE
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ---- Layout style consts (match Pressure Plate's chrome dimensions) ----

const topBarStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 18px",
  height: 44,
  borderBottom: "1px solid var(--line)",
  background: "rgba(11,14,22,0.7)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "240px 1fr 240px",
  gap: 0,
  flex: 1,
  minHeight: 0,
};

const leftPanelStyle: CSSProperties = {
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  background: "rgba(7,9,15,0.6)",
  borderRight: "1px solid var(--line)",
  minHeight: 0,
  overflowY: "auto",
};

const rightPanelStyle: CSSProperties = {
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  background: "rgba(7,9,15,0.6)",
  borderLeft: "1px solid var(--line)",
  minHeight: 0,
  overflowY: "auto",
};

const mainStyle: CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "10px 16px",
  gap: 8,
  minHeight: 0,
  overflow: "hidden",
};

const footerStyle: CSSProperties = {
  // 4 slots in priority order: fairness · cashout (optional) · main-cta · hints.
  // Cashout slot uses `auto` so it collapses to 0 width when not rendered;
  // the main CTA still gets the 1fr stretch.
  display: "grid",
  gridTemplateColumns: "auto auto 1fr auto",
  gap: 12,
  alignItems: "center",
  padding: "6px 14px",
  background: "rgba(11,14,22,0.85)",
  borderTop: "1px solid var(--line)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  position: "sticky",
  bottom: 0,
  zIndex: 10,
};
